import { useEffect, useRef, useState } from 'react';
import { shelfSnapOffset } from '../services/ebook-shelf-drag';

/** Pointer state is local presentation state; book navigation stays in React. */
export function useBookshelfRail({
  count,
  enabled,
  initialIndex,
  onIndexChange,
}: {
  count: number;
  enabled: boolean;
  initialIndex: number;
  onIndexChange(index: number): void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState({ stride: 200, visible: 3, index: initialIndex, max: 0 });
  const latest = useRef({ count, enabled, initialIndex, onIndexChange });
  latest.current = { count, enabled, initialIndex, onIndexChange };
  const moveTo = useRef<(left: number) => void>(() => {});
  useEffect(() => {
    const node = host.current;
    if (!node) return;
    let stride = 200,
      visible = 3,
      max = 0,
      initialized = false,
      suppressed = false;
    let drag: {
      id: number;
      x: number;
      y: number;
      left: number;
      moved: boolean;
      last: number;
      time: number;
      velocity: number;
    } | null = null;
    const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sync = () => {
      node.parentElement?.style.setProperty('--shelf-scroll', `${node.scrollLeft}px`);
      const index = Math.max(
        0,
        Math.min(Math.max(0, count - visible), Math.round(node.scrollLeft / stride)),
      );
      setMetrics({ stride, visible, index, max });
      latest.current.onIndexChange(index);
    };
    const scrollTo = (left: number) => {
      node.scrollTo({
        left: Math.max(0, Math.min(max, left)),
        behavior: reduced() ? 'instant' : 'smooth',
      });
    };
    moveTo.current = scrollTo;
    const measure = () => {
      if (!node.clientWidth) return;
      visible = Number.parseFloat(getComputedStyle(node).getPropertyValue('--visible-books')) || 3;
      const inset =
        Number.parseFloat(getComputedStyle(node).getPropertyValue('--rail-inset-ratio')) || 0;
      stride = (node.clientWidth * (1 - 2 * inset) + 20) / visible;
      max = Math.max(0, (count - visible) * stride);
      node.parentElement?.style.setProperty('--shelf-slot', `${stride}px`);
      const track = node.firstElementChild as HTMLElement | null;
      if (track) track.style.width = `${node.clientWidth + max}px`;
      if (!initialized) {
        node.scrollLeft = Math.min(max, latest.current.initialIndex * stride);
        initialized = true;
      } else node.scrollLeft = Math.min(max, latest.current.initialIndex * stride);
      sync();
    };
    const release = () => {
      const current = drag;
      drag = null;
      node.classList.remove('is-dragging');
      if (current && node.hasPointerCapture(current.id)) node.releasePointerCapture(current.id);
      return current;
    };
    const cancel = () => {
      const current = release();
      if (current?.moved) scrollTo(current.left);
    };
    const down = (e: PointerEvent) => {
      if (!latest.current.enabled || e.button !== 0 || !e.isPrimary) return;
      suppressed = false;
      drag = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        left: node.scrollLeft,
        moved: false,
        last: node.scrollLeft,
        time: e.timeStamp,
        velocity: 0,
      };
    };
    const move = (e: PointerEvent) => {
      if (!drag || drag.id !== e.pointerId) return;
      if (!latest.current.enabled) {
        cancel();
        return;
      }
      const dx = e.clientX - drag.x,
        dy = e.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) >= 7) {
        if (Math.abs(dy) > Math.abs(dx) * 1.2) {
          cancel();
          return;
        }
        drag.moved = true;
        suppressed = true;
        node.classList.add('is-dragging');
        node.setPointerCapture(e.pointerId);
      }
      if (!drag.moved) return;
      if (e.cancelable) e.preventDefault();
      const next = Math.max(0, Math.min(max, drag.left - dx));
      node.scrollLeft = next;
      drag.velocity = (next - drag.last) / Math.max(1, e.timeStamp - drag.time);
      drag.last = next;
      drag.time = e.timeStamp;
    };
    const up = (e: PointerEvent) => {
      if (!drag || drag.id !== e.pointerId) return;
      const current = release();
      if (!current?.moved) return;
      const velocity = e.timeStamp - current.time < 100 ? current.velocity : 0;
      scrollTo(shelfSnapOffset(node.scrollLeft, velocity, stride, max));
    };
    const click = (e: MouseEvent) => {
      if (suppressed && e.detail !== 0) {
        e.preventDefault();
        e.stopPropagation();
      }
      suppressed = false;
    };
    // Touch initially captures the child implicitly. Transferring capture to
    // the rail bubbles a lost event from that child; it is not a cancellation.
    const lost = (e: PointerEvent) => {
      if (e.target === node) cancel();
    };
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    node.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', cancel);
    node.addEventListener('lostpointercapture', lost);
    node.addEventListener('click', click, true);
    node.addEventListener('scroll', sync, { passive: true });
    measure();
    return () => {
      release();
      observer.disconnect();
      node.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', cancel);
      node.removeEventListener('lostpointercapture', lost);
      node.removeEventListener('click', click, true);
      node.removeEventListener('scroll', sync);
    };
  }, [count]);
  return {
    host,
    ...metrics,
    start: Math.max(0, metrics.index - 1),
    end: Math.min(count, metrics.index + Math.ceil(metrics.visible) + 2),
    to(index: number) {
      if (latest.current.enabled) moveTo.current(index * metrics.stride);
    },
    reveal(index: number) {
      if (!latest.current.enabled) return;
      if (index < metrics.index) moveTo.current(index * metrics.stride);
      else if (index >= metrics.index + metrics.visible)
        moveTo.current((index - metrics.visible + 1) * metrics.stride);
    },
    go(direction: number) {
      if (latest.current.enabled)
        moveTo.current((host.current?.scrollLeft ?? 0) + direction * metrics.stride);
    },
  };
}
